import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { DxTradeAccountMetricsResponse } from '@model/DxTrade.types'

import { getMetricsStoreSnapshot, subscribeToMetricsStore } from './useDxtradeAccountWebSocket'

export const useDxtradeAccountMetrics = (
  accountCode: string
): {
  data: DxTradeAccountMetricsResponse | undefined
  isLoading: boolean
  forceRefresh: (skipLoadingFlag?: boolean) => void
} => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const wsAccountKey = accountCode ? `default:${accountCode}` : ''

  const subscribe = useCallback(
    (listener: () => void) => subscribeToMetricsStore(wsAccountKey, listener),
    [wsAccountKey]
  )
  const wsMetrics = useSyncExternalStore(subscribe, () => getMetricsStoreSnapshot(wsAccountKey))

  const { data: restData, isLoading: restIsLoading } = useQuery({
    queryKey: queryKeys.trade.dxtrade.accountMetrics(accountCode),
    queryFn: () => apiClient.getDxtradeAccountMetrics(accountCode, true),
    enabled: !!accountCode,
  })

  const data = useMemo<DxTradeAccountMetricsResponse | undefined>(() => {
    if (!restData && !wsMetrics) {
      return undefined
    }

    if (!wsMetrics) {
      return restData
    }

    return {
      metrics: [wsMetrics],
    }
  }, [restData, wsMetrics])

  const isLoading = wsMetrics ? false : restIsLoading

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.trade.dxtrade.accountMetrics(accountCode),
    })
  }, [queryClient, accountCode])

  return { data, isLoading, forceRefresh }
}
